import type { PhysicsEngine } from '../physics/PhysicsEngine';
import {
  AVBD_DEFAULT_PROFILE_ID,
  AVBD_OFFICIAL_EXACT_PROFILE,
  AVBD_PROFILES,
  AVBD_REFERENCE_ANGULAR_DUAL_UPDATE_BETA,
  AVBD_REFERENCE_DUAL_UPDATE_BETA,
  AVBD_REFERENCE_PAIR_SWEEPS,
  AVBD_REFERENCE_PENALTY_DECAY_GAMMA,
  AVBD_REFERENCE_PENALTY_FLOOR,
  AVBD_REFERENCE_REGULARIZATION_ALPHA,
  AVBD_REFERENCE_SOLVER_ITERATIONS,
  AVBD_REFERENCE_SUBSTEPS,
  type AvbdParameterProfile,
  type AvbdExecutionPolicy,
  type AvbdProfileId,
} from '../physics/avbdParams';

// Official avbd-demo3d profile. Re-export the physics-layer source of truth
// for UI and regression callers; source-specialized WGSL imports the same
// values. The paper's beta=10, alpha=.95, gamma=.99 profile is exposed
// separately by the profile layer rather than mixed into these 3D defaults.
export {
  AVBD_DEFAULT_PROFILE_ID,
  AVBD_OFFICIAL_EXACT_PROFILE,
  AVBD_PROFILES,
  AVBD_REFERENCE_ANGULAR_DUAL_UPDATE_BETA,
  AVBD_REFERENCE_DUAL_UPDATE_BETA,
  AVBD_REFERENCE_PAIR_SWEEPS,
  AVBD_REFERENCE_PENALTY_DECAY_GAMMA,
  AVBD_REFERENCE_PENALTY_FLOOR,
  AVBD_REFERENCE_REGULARIZATION_ALPHA,
  AVBD_REFERENCE_SOLVER_ITERATIONS,
  AVBD_REFERENCE_SUBSTEPS,
};

export type { AvbdExecutionPolicy, AvbdParameterProfile, AvbdProfileId };

export function isAvbdProfileId(value: string | null): value is AvbdProfileId {
  return value === 'production-robust'
    || value === 'official-demo3d'
    || value === 'paper-2025'
    || value === 'official-exact';
}

export function getAvbdProfile(id: AvbdProfileId): AvbdParameterProfile {
  return AVBD_PROFILES[id];
}

export function applyAvbdProfile(
  physics: PhysicsEngine,
  profileOrId: AvbdParameterProfile | AvbdProfileId,
): AvbdParameterProfile {
  const profile = typeof profileOrId === 'string'
    ? getAvbdProfile(profileOrId)
    : profileOrId;
  physics.setSubsteps(profile.substeps);
  physics.setSolverIterations(profile.solverIterations);
  physics.setAvbdDualUpdateBetas(
    profile.linearDualUpdateBeta,
    profile.angularDualUpdateBeta,
  );
  physics.setAvbdRegularizationAlpha(profile.regularizationAlpha);
  physics.setAvbdPenaltyDecayGamma(profile.penaltyDecayGamma);
  physics.setAvbdPairSweeps(profile.pairSweeps);
  physics.setAvbdPreventPenetratingNormalDropout(false);
  physics.setAvbdPenaltyFloor(profile.penaltyFloor);
  physics.setAvbdBodySolveMode(profile.executionPolicy?.bodySolveMode ?? 'colored');
  if (profile.executionPolicy) {
    physics.setAvbdAdaptiveIterationsConfigured(
      profile.executionPolicy.adaptiveIterations,
    );
  }
  return profile;
}

export function applyReferenceAvbdConfig(physics: PhysicsEngine): void {
  applyAvbdProfile(physics, AVBD_DEFAULT_PROFILE_ID);
}
